import { MessageChatCircle, Plus } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { CollapseToggleButton } from '@/components/navigation/CollapseToggleButton';
import { Button } from '@/lib/base/buttons/button';
import { Tab, TabList, Tabs } from '@/lib/base/tabs';
import {
  builderConversationPath,
  useBuilderMode,
} from '@/lib/crm/hooks/useBuilderMode';
import { useEnsuredStoreObjects } from '@/lib/crm/hooks/useEnsuredStoreObjects';
import { fetchWithDefaults } from '@/lib/http/client';
import { getBuilderRowLabel } from '@/lib/reasoning/builderRowLabel';
import { ConversationRunStateLabel } from '@/lib/reasoning/components/ConversationRunStateLabel';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';
import { useBuilderConversations } from '@/lib/reasoning/hooks/useBuilderConversations';
import type { CrmObject } from '@/types/crm';

interface ConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: ConversationLifecycleState;
  created_at: string;
  updated_at: string;
  has_active_chain?: boolean;
  last_chain_failed?: boolean;
}

type HistoryMode = 'chats' | 'builds';

// Component with typewriter effect for the row label
function ConversationTitleButton({
  label,
  isSelected,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [displayTitle, setDisplayTitle] = useState(label);
  const prevTitleRef = useRef<string>(label);

  useEffect(() => {
    const newTitle = label;
    const prevTitle = prevTitleRef.current;

    // Only animate if title changed from "New Conversation" to something else
    if (prevTitle === 'New Conversation' && newTitle !== 'New Conversation') {
      setDisplayTitle('');
      let i = 0;
      const timer = setInterval(() => {
        if (i < newTitle.length) {
          setDisplayTitle(newTitle.slice(0, i + 1));
          i++;
        } else {
          clearInterval(timer);
        }
      }, 25);

      prevTitleRef.current = newTitle;
      return () => clearInterval(timer);
    } else {
      setDisplayTitle(newTitle);
      prevTitleRef.current = newTitle;
    }
  }, [label]);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 text-sm truncate transition-colors cursor-pointer ${
        isSelected
          ? 'bg-bg-secondary text-primary font-medium'
          : 'text-secondary hover:bg-bg-secondary_hover hover:text-primary'
      }`}
      title={label}
    >
      {displayTitle}
    </button>
  );
}

interface GroupedConversations<T> {
  label: string;
  conversations: T[];
}

// Group conversations by relative time periods
function groupConversationsByDate<T extends { created_at: string }>(
  conversations: T[]
): GroupedConversations<T>[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const lastMonth = new Date(today);
  lastMonth.setDate(lastMonth.getDate() - 30);

  const groups: Record<string, T[]> = {
    Today: [],
    Yesterday: [],
    'Previous 7 days': [],
    'Previous 30 days': [],
    Older: [],
  };

  conversations.forEach(conv => {
    const convDate = new Date(conv.created_at);
    const convDay = new Date(
      convDate.getFullYear(),
      convDate.getMonth(),
      convDate.getDate()
    );

    if (convDay.getTime() >= today.getTime()) {
      groups['Today'].push(conv);
    } else if (convDay.getTime() >= yesterday.getTime()) {
      groups['Yesterday'].push(conv);
    } else if (convDay.getTime() >= lastWeek.getTime()) {
      groups['Previous 7 days'].push(conv);
    } else if (convDay.getTime() >= lastMonth.getTime()) {
      groups['Previous 30 days'].push(conv);
    } else {
      groups['Older'].push(conv);
    }
  });

  // Return only non-empty groups
  return Object.entries(groups)
    .filter(([, convs]) => convs.length > 0)
    .map(([label, conversations]) => ({ label, conversations }));
}

/** Uniform row shape the grouped list renders, regardless of source list. */
interface HistoryRow {
  id: string;
  label: string;
  created_at: string;
  lastChainFailed?: boolean;
  hasActiveChain?: boolean;
  onClick: () => void;
}

export interface ConversationHistoryPanelProps {
  /** Collapsed to the 40px edge strip (user toggle, persisted to localStorage). */
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

export default function ConversationHistoryPanel({
  collapsed,
  onToggleCollapsed,
}: ConversationHistoryPanelProps) {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const location = useLocation();
  const { isBuilderMode } = useBuilderMode();
  const [conversations, setConversations] = useState<ConversationListItem[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const hasLoadedRef = useRef(false);
  const [mode, setMode] = useState<HistoryMode>('chats');

  // Only show on conversation pages
  const isConversationPage = location.pathname.includes('/conversations');

  // Builder mode suppresses the history list to keep the object in focus.
  // Gated here as well as in DashboardLayout so the fetch and the 2s title
  // poll below stop too — not just the render.
  const shouldShowHistory = isConversationPage && !isBuilderMode;

  // Extract conversationId from pathname since this component is rendered outside the route
  const conversationId = location.pathname.match(
    /\/conversations\/([^/]+)/
  )?.[1];

  const loadConversations = useCallback(async () => {
    if (!orgId) {
      return;
    }
    try {
      // Only show skeleton on initial load, not on subsequent polls/refreshes
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      const response = await fetchWithDefaults(
        `/api/orgs/${orgId}/reasoning/conversations/`
      );
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
      hasLoadedRef.current = true;
    }
  }, [orgId]);

  // Load conversations on mount and when conversation changes
  useEffect(() => {
    if (!orgId || !shouldShowHistory) {
      return;
    }
    loadConversations();
  }, [orgId, shouldShowHistory, conversationId, loadConversations]);

  // Poll for title updates when current conversation has "New Conversation" title
  useEffect(() => {
    if (!conversationId || !shouldShowHistory) {
      return;
    }

    const currentConv = conversations.find(
      c => String(c.id) === conversationId
    );
    // If the current conversation title is the default, poll for updates
    if (currentConv?.title === 'New Conversation') {
      const interval = setInterval(loadConversations, 2000);
      return () => clearInterval(interval);
    }
  }, [conversationId, conversations, shouldShowHistory, loadConversations]);

  // Builder conversations — fetched alongside the ordinary list so the
  // toggle's own visibility (below) can be decided without a fetch delay
  // once the user actually switches to it.
  const { data: builderData } = useBuilderConversations(orgId, {
    enabled: shouldShowHistory,
  });
  const builderConversations = useMemo(
    () => builderData?.conversations ?? [],
    [builderData]
  );
  // Gate on the resolved list length, not loading state, so the toggle does
  // not flash in and out while the fetch is in flight. An org that has never
  // used builder mode never sees a non-empty result, so it never appears.
  const hasBuilderConversations = builderConversations.length > 0;
  const effectiveMode: HistoryMode = hasBuilderConversations ? mode : 'chats';

  const limitedBuilderConversations = useMemo(
    () => builderConversations.slice(0, 15),
    [builderConversations]
  );
  const builderEntityIds = useMemo(
    () =>
      limitedBuilderConversations
        .map(c => c.builder_entity_id)
        .filter((id): id is string => !!id),
    [limitedBuilderConversations]
  );
  const orgIdNumber = orgId ? Number(orgId) : undefined;
  // Batch-resolve through the store rather than one fetch per row; only
  // ensure (fetch) while the builds tab is actually showing.
  const { objects: resolvedBuilderObjects } = useEnsuredStoreObjects(
    orgIdNumber,
    builderEntityIds,
    { ensure: effectiveMode === 'builds' }
  );
  const resolvedByEntityId = useMemo(() => {
    const map = new Map<string, CrmObject>();
    for (const resolved of resolvedBuilderObjects) {
      if (resolved.object) {
        map.set(resolved.entityId, resolved.object);
      }
    }
    return map;
  }, [resolvedBuilderObjects]);

  const handleModeChange = useCallback((key: string | number) => {
    setMode(key === 'builds' ? 'builds' : 'chats');
  }, []);

  if (!shouldShowHistory) {
    return null;
  }

  if (collapsed) {
    // Edge strip: a list of titles has no meaningful icon-rail form, so the
    // collapsed state is a bare re-expand affordance at the panel's edge.
    return (
      <aside
        className='hidden lg:flex flex-col items-center h-full fixed top-0 z-40 bg-bg-primary border-r border-border-secondary w-10 pt-4'
        style={{ left: 'var(--sidebar-width, 68px)' }}
        data-component='conversation-history-panel'
        data-state='collapsed'
      >
        <CollapseToggleButton
          collapsed
          onToggle={onToggleCollapsed}
          expandLabel='Expand chat history'
          collapseLabel='Collapse chat history'
        />
      </aside>
    );
  }

  const handleNewConversation = () => {
    navigate(`/${orgId}/conversations`);
  };

  const handleConversationClick = (id: string) => {
    navigate(`/${orgId}/conversations/${id}`);
  };

  const handleBuilderConversationClick = (id: string) => {
    navigate(builderConversationPath(orgId ?? '', id));
  };

  const handleViewMore = () => {
    navigate(`/${orgId}/conversations/history`);
  };

  const rows: HistoryRow[] =
    effectiveMode === 'builds'
      ? limitedBuilderConversations.map(conv => ({
          id: String(conv.id),
          label: getBuilderRowLabel(conv, resolvedByEntityId),
          created_at: conv.created_at,
          onClick: () => handleBuilderConversationClick(String(conv.id)),
        }))
      : conversations.slice(0, 15).map(conv => ({
          id: String(conv.id),
          label: conv.title || 'Untitled',
          created_at: conv.created_at,
          lastChainFailed: conv.last_chain_failed,
          hasActiveChain: conv.has_active_chain,
          onClick: () => handleConversationClick(String(conv.id)),
        }));
  const groupedRows = groupConversationsByDate(rows);

  return (
    <aside
      className='hidden lg:flex flex-col h-full fixed top-0 z-40 bg-bg-primary border-r border-border-secondary w-[220px]'
      style={{ left: 'var(--sidebar-width, 68px)' }}
      data-component='conversation-history-panel'
      data-state='expanded'
    >
      {/* New Conversation Button + collapse toggle */}
      <div className='px-3 py-4 flex items-center gap-2'>
        <Button
          onClick={handleNewConversation}
          color='secondary'
          size='sm'
          iconLeading={<Plus className='size-[17px] mr-[2px]' />}
          noTextPadding
          className='flex-1 flex'
        >
          <span className='relative -left-[3px]'>New chat</span>
        </Button>
        <CollapseToggleButton
          collapsed={false}
          onToggle={onToggleCollapsed}
          expandLabel='Expand chat history'
          collapseLabel='Collapse chat history'
          className='shrink-0'
        />
      </div>

      {/* Chats/Builds toggle — only offered when there is something to
          toggle to (see hasBuilderConversations above). */}
      {hasBuilderConversations && (
        <div className='px-3 pb-2'>
          <Tabs
            selectedKey={effectiveMode}
            onSelectionChange={handleModeChange}
          >
            <TabList aria-label='Conversation history filter' className='gap-4'>
              <Tab id='chats'>Chats</Tab>
              <Tab id='builds'>Builds</Tab>
            </TabList>
          </Tabs>
        </div>
      )}

      {/* Conversation List */}
      <div className='flex-1 overflow-y-auto py-2'>
        {loading && effectiveMode === 'chats' && (
          // Skeleton loading
          <div className='px-3 space-y-3'>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className='space-y-2'>
                <div className='h-3 w-16 bg-bg-secondary rounded animate-pulse' />
                <div className='h-8 bg-bg-secondary rounded animate-pulse' />
                <div className='h-8 bg-bg-secondary rounded animate-pulse' />
              </div>
            ))}
          </div>
        )}
        {!(loading && effectiveMode === 'chats') &&
          groupedRows.length === 0 && (
            <div className='px-3 py-8 text-center'>
              <MessageChatCircle className='w-8 h-8 mx-auto text-quaternary mb-2' />
              <p className='text-sm text-tertiary'>No conversations yet</p>
            </div>
          )}
        {!(loading && effectiveMode === 'chats') && groupedRows.length > 0 && (
          <div className='space-y-4'>
            {groupedRows.map(group => (
              <div key={group.label}>
                <h3 className='px-3 py-1 text-xs font-medium text-tertiary uppercase tracking-wider'>
                  {group.label}
                </h3>
                <ul className='space-y-0.5'>
                  {group.conversations.map(row => (
                    <li key={row.id}>
                      <ConversationTitleButton
                        label={row.label}
                        isSelected={conversationId === row.id}
                        onClick={row.onClick}
                      />
                      <ConversationRunStateLabel
                        lastChainFailed={row.lastChainFailed}
                        hasActiveChain={row.hasActiveChain}
                        className='block px-3 -mt-1 pb-1'
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* View all chats - inline after list, negative margin to reduce space-y-4 gap */}
            {effectiveMode === 'chats' && (
              <button
                onClick={handleViewMore}
                className='w-full text-left px-3 py-2 text-sm text-tertiary hover:bg-bg-secondary_hover hover:text-primary transition-colors cursor-pointer -mt-2'
              >
                View all chats →
              </button>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
